import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaCog, FaBook, FaFilter, FaCalendarAlt, FaUser, FaStar, FaMapMarkerAlt, FaLock, FaExclamation, FaSignOutAlt, FaCheck } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { getUserDisplayName } from '../services/authService';
import '../styles/Sidebar.css';

// New service import
import { getGuidesMenu } from '../services/guideService';


// Import SVG icons for filter markers
import culturalIcon from '../assets/icons/cultural.svg';
import documentsIcon from '../assets/icons/documents.svg';
import educationalIcon from '../assets/icons/educational.svg';
import recreationIcon from '../assets/icons/recreation.svg';
import restaurantIcon from '../assets/icons/restaurant.svg';
import shoppingIcon from '../assets/icons/shopping.svg';

// Create a new context for map interactions
export const MapInteractionContext = React.createContext();

const Sidebar = ({ cityName, onFilterChange, selectedFilters, isOpen, onClose }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('menu');
    const { currentUser, idToken: token, logout, isLoading: authIsLoading } = useAuth();
    const {
        cachedFavorites,
        loadingFavorites,
        loadUserFavorites
    } = useDataCache();
    const [displayUsername, setDisplayUsername] = useState(null);
    const [guidesMenu, setGuidesMenu] = useState([]);
    const [loadingGuides, setLoadingGuides] = useState(true);
    
    const mapInteraction = React.useContext(MapInteractionContext);

    useEffect(() => {
        let isMounted = true;
        const fetchUsername = async () => {
            if (currentUser && token) {
                if (currentUser.displayUsername) {
                    if (isMounted) setDisplayUsername(currentUser.displayUsername);
                } else {
                    try {
                        const { displayUsername: fetchedName, error } = await getUserDisplayName(currentUser.uid, token);
                        if (!error && isMounted) {
                            setDisplayUsername(fetchedName);
                        }
                    } catch (e) {
                        console.error("Error fetching display name in Sidebar:", e);
                        if (isMounted) setDisplayUsername(currentUser.email);
                    }
                }
            } else if (!currentUser && isMounted) {
                setDisplayUsername(null);
            }
        };
        fetchUsername();
        return () => { isMounted = false; };
    }, [currentUser, token]);

    useEffect(() => {
            if (currentUser && token) {
            loadUserFavorites(token);
        }
    }, [currentUser, token, loadUserFavorites]);

    useEffect(() => {
        const fetchGuidesMenu = async () => {
            try {
                setLoadingGuides(true);
                const menu = await getGuidesMenu();
                setGuidesMenu(menu);
            } catch (error) {
                console.error('Failed to fetch guides menu:', error);
            } finally {
                setLoadingGuides(false);
            }
        };

        fetchGuidesMenu();
    }, []);

    const handleBack = () => {
        setActiveTab('menu');
    };

    const handleFilterChange = (event) => {
        const { name, checked } = event.target;
        onFilterChange(name, checked);
    };

    const handleFavoriteClick = (favorite, e) => {
        e.preventDefault();
        if (favorite.cityName === cityName) {
            if (mapInteraction && mapInteraction.showLocationPopup) {
                mapInteraction.showLocationPopup(favorite.locationId);
                setActiveTab('menu');
            }
        } else {
            navigate(`/city/${favorite.cityName}`);
        }
    };

    const handleLogout = async () => {
        await logout();
        setActiveTab('menu');
        navigate('/');
    };

    const renderMenu = () => (
        <div className="menu">
            <div className="sidebar-header">
                <div className="city-header">
                    <h2>{cityName.charAt(0).toUpperCase() + cityName.slice(1)}</h2>
                    {displayUsername && (
                        <span className="username-display">• {displayUsername}</span>
                    )}
                </div>
                {/* The close button is now handled outside this render function for better mobile layout */}
            </div>
            <button onClick={() => navigate('/')} className="back-button">
                <FaArrowLeft /> Back to Cities
            </button>
            <ul>
                <li onClick={() => setActiveTab('guides')}><FaBook /> Guides</li>
                <li onClick={() => setActiveTab('filters')}><FaFilter /> Filters</li>
                <li onClick={() => setActiveTab('favorites' )}><FaStar /> Favorites</li>
                <li onClick={() => setActiveTab('events' )}><FaCalendarAlt /> Events & News</li>
                <li onClick={() => {
                    if (currentUser) {
                        setActiveTab('account');
                    } else {
                        navigate('/auth');
                    }
                }}>
                    <FaUser /> {currentUser ? 'Account' : 'Login/Register'}
                </li>
                <li onClick={() => setActiveTab('settings')} className="settings-menu-item"><FaCog /> Settings</li>
            </ul>
        </div>
    );

    const renderFilters = () => (
        <div>
            <button onClick={handleBack} className="back-button">
                <FaArrowLeft /> Back
            </button>
            <h3>Filters</h3>
            <ul className="filter-list">
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="documents"
                            checked={selectedFilters.documents}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#0081a7' }}>
                                <img src={documentsIcon} className="marker-icon-mini" alt="documents" />
                            </div>
                        </div>
                        <span>Documents</span>
                    </label>
                </li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="shops"
                            checked={selectedFilters.shops}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#75E6DA' }}>
                                <img src={shoppingIcon} className="marker-icon-mini" alt="shops" />
                            </div>
                        </div>
                        <span>Shops</span>
                    </label>
                </li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="restaurants"
                            checked={selectedFilters.restaurants}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#ff6150' }}>
                                <img src={restaurantIcon} className="marker-icon-mini" alt="restaurants" />
                            </div>
                        </div>
                        <span>Restaurants</span>
                    </label>
                </li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="recreation"
                            checked={selectedFilters.recreation}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#f8aa4b' }}>
                                <img src={recreationIcon} className="marker-icon-mini" alt="recreation" />
                            </div>
                        </div>
                        <span>Recreation</span>
                    </label>
                </li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="cultural"
                            checked={selectedFilters.cultural}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#ddacf5' }}>
                                <img src={culturalIcon} className="marker-icon-mini" alt="cultural" />
                            </div>
                        </div>
                        <span>Cultural</span>
                    </label>
                </li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="educational"
                            checked={selectedFilters.educational}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#ffcb00' }}>
                                <img src={educationalIcon} className="marker-icon-mini" alt="educational" />
                            </div>
                        </div>
                        <span>Educational</span>
                    </label>
                </li>
                <li className="filter-divider"></li>
                <li>
                    <label className="filter-label">
                        <input
                            type="checkbox"
                            name="important"
                            checked={selectedFilters.important}
                            onChange={handleFilterChange}
                        />
                        <div className="filter-marker important-filter-marker">
                            <div className="marker-circle-mini" style={{ backgroundColor: '#4CAF50' }}>
                                <FaExclamation className="marker-icon-mini universal-icon" />
                                <div className="important-marker-badge-mini"></div>
                            </div>
                        </div>
                        <span>Important Locations</span>
                    </label>
                </li>
            </ul>
        </div>
    );

    const renderGuides = () => {
        const generalGuides = guidesMenu.general || [];
        const cityGuides = (guidesMenu.cities || []).filter(
            guide => guide.city && guide.city.toLowerCase() === cityName.toLowerCase()
        );

        // Create a consistent structure for the GuideMenuItem component to consume
        const processedMenu = [];
        if (generalGuides.length > 0) {
            processedMenu.push({
                name: 'General',
                type: 'folder',
                children: generalGuides,
            });
        }
        processedMenu.push({
            name: `${cityName.charAt(0).toUpperCase() + cityName.slice(1)} Guides`,
            type: 'folder',
            children: cityGuides,
        });

        return (
            <div>
                <button onClick={handleBack} className="back-button">
                    <FaArrowLeft /> Back
                </button>
                <h3>Guides</h3>
                {loadingGuides ? (
                    <p>Loading guides...</p>
                ) : (
                    <ul className="guides-menu">
                        {processedMenu.map((item, index) => (
                            <GuideMenuItem key={index} item={item} />
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const GuideMenuItem = ({ item }) => {
        const [isOpen, setIsOpen] = useState(true); // Changed to true to be open by default

        if (item.type === 'folder') {
            return (
                <li>
                    <div onClick={() => setIsOpen(!isOpen)} className="guide-folder">
                        {item.name}
                    </div>
                    {isOpen && (
                        <ul className="guides-submenu">
                            {item.children.map((child, index) => (
                                <GuideMenuItem key={index} item={child} />
                            ))}
                        </ul>
                    )}
                </li>
            );
        }

        return (
            <li>
                <Link to={`/guide/${encodeURIComponent(item.path)}?from=${cityName}`} className="guide-link">
                    {item.emoji && <span className="guide-emoji">{item.emoji}</span>}
                    <span className="guide-name">{item.name}</span>
                </Link>
            </li>
        );
    };

    const renderFavorites = () => {
        if (!currentUser && !authIsLoading) {
            return (
                <div className="favorites-container">
                    <button onClick={handleBack} className="back-button">
                        <FaArrowLeft /> Back
                    </button>
                    <h3><FaStar /> Favorites</h3>
                    <div className="login-prompt">
                        <FaLock className="lock-icon" />
                        <p>Please log in to view your favorites.</p>
                        <Link to="/auth" className="login-button" style={{ textDecoration: 'none' }}>
                            Login or Register
                        </Link>
                    </div>
                </div>
            );
        }

        const cityFavorites = cachedFavorites.filter(fav => fav.cityName === cityName);

        return (
        <div className="favorites-container">
            <button onClick={handleBack} className="back-button">
                <FaArrowLeft /> Back
            </button>
            <h3><FaStar /> Favorites</h3>
            {loadingFavorites ? (
                <p>Loading favorites...</p>
                ) : cityFavorites.length > 0 ? (
                <ul className="favorites-list">
                        {cityFavorites.map(fav => (
                        <li key={`${fav.cityName}-${fav.locationId}`}>
                            <button className="favorite-button" onClick={(e) => handleFavoriteClick(fav, e)}>
                                <FaMapMarkerAlt /> {fav.locationName} 
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                    <p>You haven't added any favorites in {cityName.charAt(0).toUpperCase() + cityName.slice(1)} yet.</p>
            )}
        </div>
    );
    };

    const renderEvents = () => (
        <div>
            <button onClick={handleBack} className="back-button">
                <FaArrowLeft /> Back
            </button>
            <h3>Events & News</h3>
            <p>Placeholder for events & news content.</p>
        </div>
    );

    const renderAccount = () => (
        <div className="account-container">
            <button onClick={handleBack} className="back-button">
                <FaArrowLeft /> Back
            </button>
            <h3>Account</h3>
            {currentUser ? (
                <div className="account-details">
                    <p><strong>Username:</strong> {displayUsername || 'Loading...'}</p>
                    <p><strong>Email:</strong> {currentUser.email}</p>
                    <p><strong>Status:</strong> {currentUser.is_verified_student ? 
                        <span className="verified"><FaCheck/> Verified Student</span> : 
                        <span className="not-verified"><FaLock/> Not Verified</span>}
                    </p>
                    {!currentUser.is_verified_student && (
                        <Link to="/verify" className="verify-button" onClick={() => setActiveTab('menu')}>
                            Get Verified
                        </Link>
                    )}
                    <button onClick={handleLogout} className="logout-button">
                        <FaSignOutAlt /> Logout
                    </button>
                </div>
            ) : (
                <p>You are not logged in. <Link to="/auth">Login or Register</Link></p>
            )}
        </div>
    );

    const renderSettings = () => (
        <div>
            <button onClick={handleBack} className="back-button">
                <FaArrowLeft /> Back
            </button>
            <h3>Settings</h3>
            <p>Placeholder for settings content.</p>
        </div>
    );

    return (
        <>
            <div className={`sidebar ${isOpen ? 'open' : ''}`}>
                {activeTab === 'menu' && renderMenu()}
                {activeTab === 'filters' && renderFilters()}
                {activeTab === 'guides' && renderGuides()}
                {activeTab === 'favorites' && renderFavorites()}
                {activeTab === 'events' && renderEvents()}
                {activeTab === 'account' && renderAccount()}
                {activeTab === 'settings' && renderSettings()}
            </div>
            <button onClick={onClose} className="sidebar-mobile-close">
                <FaArrowLeft />
            </button>
        </>
    );
};

export default Sidebar;